import { memo, useCallback, useEffect, useRef, useState, type MouseEvent, type ReactNode } from "react";
import { BookmarkCheck, BookmarkPlus, Maximize2, Pause, Play, Volume2, VolumeX } from "lucide-react";
import RemixMark from "@/app/routes/dashboard/components/icons/RemixMark";
import PlatformBadge from "@/app/routes/dashboard/components/PlatformBadge";
import { Pill } from "@/app/routes/dashboard/components/ds";
import { cn } from "@/utils/helpers";
import type { Trend, TrendPreviewVideo } from "@/types/trend.type";
import {
  compactAge,
  compactTrendNum,
  countedFigures,
  figuresSentence,
  originLabel,
  platformLabel,
  trendPlatforms,
  TYPE_META,
} from "../components/trendMeta";

/** Every clip this item can swipe through: the feed's `previewVideos` when the
 *  route sent them, else the one preview it always sends. */
export function feedClips(trend: Trend): TrendPreviewVideo[] {
  if (trend.previewVideos && trend.previewVideos.length > 0) return trend.previewVideos;
  return trend.previewVideo ? [trend.previewVideo] : [];
}

/**
 * One clip on the stage. A <video> only while the item is on screen or next to
 * it (the rest are stills, so a long feed never holds a hundred videos), and it
 * plays only when the feed says so. Muting is done to the element, and a play
 * that a browser refuses WITH sound is retried muted rather than left frozen.
 * A dead platform poster (they are signed and expire) falls back to the clip's
 * own first frame, as the wall's plates do.
 */
function FeedClip({
  clip,
  mounted,
  playing,
  muted,
  register,
}: {
  clip: TrendPreviewVideo;
  mounted: boolean;
  playing: boolean;
  muted: boolean;
  /** The clip on the stage hands its element up, so a click can unmute it
   *  inside the gesture itself. */
  register?: (el: HTMLVideoElement | null) => void;
}) {
  const ref = useRef<HTMLVideoElement | null>(null);
  const [deadPoster, setDeadPoster] = useState<string | null>(null);
  const [imageFailed, setImageFailed] = useState(false);
  const poster = clip.thumbnailUrl && clip.thumbnailUrl !== deadPoster ? clip.thumbnailUrl : null;
  const playable = Boolean(clip.videoUrl) && !clip.videoBroken;

  const setRef = useCallback(
    (el: HTMLVideoElement | null) => {
      ref.current = el;
      register?.(el);
    },
    [register],
  );

  useEffect(() => {
    if (!clip.thumbnailUrl) return;
    const probe = document.createElement("img");
    const url = clip.thumbnailUrl;
    /* Asked for the way the stills ask, with no referrer, and decoded off the
       main thread: nothing paints this image, it only reports. */
    probe.referrerPolicy = "no-referrer";
    probe.decoding = "async";
    probe.onerror = () => setDeadPoster(url);
    probe.src = url;
    return () => {
      probe.onerror = null;
    };
  }, [clip.thumbnailUrl]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.muted = muted;
  }, [muted, mounted]);

  /* The latest intent, read by a play() that settles later: a pause that
     arrives while a clip is still buffering must win over the retry below. */
  const playingRef = useRef(playing);
  useEffect(() => {
    playingRef.current = playing;
  });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (playing) {
      void el.play().catch((error: unknown) => {
        /* Only a browser REFUSING sound outside a gesture is retried, muted,
           so the picture keeps moving and the next tap brings the sound back.
           An AbortError is a pause() that interrupted this play() (a tap, the
           detail opening), and retrying it would resume against the viewer.
           And never once the viewer no longer wants it playing. */
        if ((error as { name?: string } | null)?.name !== "NotAllowedError") return;
        if (!playingRef.current || el.muted) return;
        el.muted = true;
        void el.play().catch(() => {});
      });
    } else {
      el.pause();
    }
  }, [playing, mounted]);

  /* No poster to paint: seek a hair past zero once metadata is in, so the stage
     shows the clip's first frame instead of black. */
  useEffect(() => {
    const el = ref.current;
    if (!el || poster) return;
    const paintFirstFrame = () => {
      if (el.paused && el.currentTime === 0) el.currentTime = 0.1;
    };
    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) paintFirstFrame();
    else el.addEventListener("loadedmetadata", paintFirstFrame, { once: true });
    return () => el.removeEventListener("loadedmetadata", paintFirstFrame);
  }, [poster, mounted, clip.videoUrl]);

  if (mounted && playable) {
    return (
      <video
        ref={setRef}
        className="tf-media"
        src={clip.videoUrl ?? undefined}
        poster={poster ?? undefined}
        muted
        loop
        playsInline
        preload="metadata"
      />
    );
  }
  if (clip.thumbnailUrl && !imageFailed) {
    return (
      <img
        className="tf-media"
        src={clip.thumbnailUrl}
        alt=""
        referrerPolicy="no-referrer"
        onError={() => setImageFailed(true)}
      />
    );
  }
  return (
    <span className="tf-media tf-media--none">
      <span className="tt-label uppercase">No preview captured</span>
    </span>
  );
}

/**
 * One control in the rail island: an icon, no word under it. The word used to
 * sit below each button, and the rail was as wide as its widest word, so
 * "Sound off" turning into "Sound on" slid all four buttons sideways. The name
 * lives in aria-label, which is where a screen reader reads it anyway.
 *
 * `on` is a state the viewer set (sound on, saved): it draws the inner fill,
 * the island's version of a selected tab. `done` is an action already taken
 * (saved): it reads as on without claiming to toggle, and pressing it again
 * says where the save can be undone. It is never dimmed like a control that
 * cannot work.
 */
function RailButton({
  onClick,
  on,
  done,
  disabled,
  children,
  name,
}: {
  /** The accessible name: what pressing it does, or, for a toggle that
   *  carries `on`, the fixed name of the thing it switches ("Sound"). */
  name: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  on?: boolean;
  done?: boolean;
  disabled?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      className="tf-rail-button"
      data-on={on || done ? "" : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onClick(event);
      }}
      aria-pressed={done ? undefined : on}
      aria-label={name}
      disabled={disabled}
    >
      {children}
    </button>
  );
}

/** How long the rail island stays at full size after the viewer last tapped
 *  the trend: long enough to find an action, short enough that it steps back
 *  before the clip has played a few seconds. */
const RAIL_AWAKE_MS = 2500;

/**
 * One trend, one screen. The stage runs the full width of the phone and every
 * clip is contained on black, so no frame is ever cut; the words and the
 * controls sit on it in media glass and over a bottom scrim, and the figures
 * are in a strip under it, exactly as the card prints them.
 */
function PhoneFeedItem({
  trend,
  index,
  total,
  active,
  near,
  playing,
  soundOn,
  isNew,
  saved,
  canSave,
  onToggleSound,
  onTapStage,
  glyph,
  onSave,
  onRecreate,
  onDetail,
}: {
  trend: Trend;
  index: number;
  /** Trends loaded so far. */
  total: number;
  /** 60% or more of this item is on screen. */
  active: boolean;
  /** This item or its neighbour is the active one: its clips are videos. */
  near: boolean;
  /** The feed says the active clip should be moving. */
  playing: boolean;
  soundOn: boolean;
  isNew: boolean;
  saved: boolean;
  canSave: boolean;
  onToggleSound: (element: HTMLVideoElement | null) => void;
  onTapStage: () => void;
  /** The glyph a tap just flashed, or null. */
  glyph: "play" | "pause" | null;
  onSave: (trend: Trend) => void;
  /** Absent for roles that can't open a Workshop project. */
  onRecreate?: (trend: Trend) => void;
  onDetail: (id: string) => void;
}) {
  const clips = feedClips(trend);
  const trackRef = useRef<HTMLDivElement>(null);
  const [slide, setSlide] = useState(0);
  const currentVideo = useRef<HTMLVideoElement | null>(null);
  const registerCurrent = useCallback((el: HTMLVideoElement | null) => {
    currentVideo.current = el;
  }, []);

  /* The dots follow the finger: the slide is read off the track's scroll. */
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    const onScroll = () => {
      const width = el.clientWidth;
      if (!width) return;
      const next = Math.max(0, Math.min(clips.length - 1, Math.round(Math.abs(el.scrollLeft) / width)));
      setSlide((current) => (current === next ? current : next));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [clips.length]);

  const { views, likes } = countedFigures(trend);
  const { label: kindLabel, icon: KindIcon } = TYPE_META[trend.type];
  const origin = originLabel(trend);
  /* The card's and the sheet's rule, so all three name the same platforms. */
  const platforms = trendPlatforms(trend) ?? [];
  const current = clips[Math.min(slide, clips.length - 1)];
  const canHear = Boolean(current?.videoUrl) && !current?.videoBroken;

  /* THE ISLAND IS PASSIVE UNTIL IT IS ASKED FOR. It rests small while the
     clip plays, the way a platform's tab bar steps back while you watch, and
     a tap on this trend (the stage or the island) or keyboard focus in it
     brings it to full size; RAIL_AWAKE_MS after the last tap it steps back
     again. Moving to another video, a trend up or down or an example to the
     side, puts it straight back to rest: a swipe is watching, not reaching.
     `wakes` counts every tap, so each one restarts the countdown. */
  const [asleep, setAsleep] = useState(true);
  const [wakes, setWakes] = useState(0);
  const railFocused = useRef(false);
  const wake = useCallback(() => {
    setAsleep(false);
    setWakes((n) => n + 1);
  }, []);
  /* Arriving on a trend, leaving one, or changing example puts the island to
     rest. Adjusted while rendering, the pattern React documents for resetting
     state when an input changes, so a woken island never lingers a frame. */
  const [restKey, setRestKey] = useState(`${active}:${slide}`);
  if (restKey !== `${active}:${slide}`) {
    setRestKey(`${active}:${slide}`);
    setAsleep(true);
  }
  useEffect(() => {
    if (!active || asleep) return;
    const timer = window.setTimeout(() => {
      if (!railFocused.current) setAsleep(true);
    }, RAIL_AWAKE_MS);
    return () => window.clearTimeout(timer);
  }, [active, asleep, wakes]);

  return (
    <article
      className="tf-item"
      data-index={index}
      aria-label={trend.title}
      /* A click, not a pointerdown: the browser sends no click after a swipe,
         so scrolling to another trend or example never wakes the island, and
         capture means a tap on the stage and a press on the island both
         count, whatever stops them further down. */
      onClickCapture={wake}
    >
      <div className="tf-stage" onClick={onTapStage}>
        <div ref={trackRef} className="tf-track">
          {clips.length > 0 ? (
            clips.map((clip, i) => (
              <div key={`${clip.videoUrl ?? clip.thumbnailUrl ?? "clip"}-${i}`} className="tf-slide">
                <FeedClip
                  clip={clip}
                  mounted={near}
                  playing={active && playing && i === slide}
                  muted={!(active && soundOn && i === slide)}
                  register={active && i === slide ? registerCurrent : undefined}
                />
              </div>
            ))
          ) : (
            <div className="tf-slide">
              <span className="tf-media tf-media--none">
                <span className="tt-label uppercase">No preview captured</span>
              </span>
            </div>
          )}
        </div>

        {/* The tap is a pointer's way to pause; this is a keyboard's. */}
        {active ? (
          <button
            type="button"
            className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:top-14 focus:z-10"
            onClick={(event) => {
              event.stopPropagation();
              onTapStage();
            }}
          >
            {playing ? "Pause" : "Play"} {trend.title}
          </button>
        ) : null}

        {glyph ? (
          <span className="tf-glyph co-media-chip" aria-hidden="true">
            {glyph === "play" ? <Play className="size-5" fill="currentColor" /> : <Pause className="size-5" fill="currentColor" />}
          </span>
        ) : null}

        <div className="tf-top">
          <div className="tf-top-start">
            {isNew ? <Pill onMedia>New</Pill> : null}
            <span className="co-media-chip co-media-chip--label tf-kind">
              <KindIcon className="size-[13px] shrink-0" strokeWidth={2.25} aria-hidden="true" />
              <span className="tf-kind-words">
                {kindLabel}
                {origin ? ` · ${origin}` : ""}
              </span>
            </span>
          </div>
          <div className="tf-top-end">
            {platforms.map((platform) => (
              <PlatformBadge key={platform} platform={platform} label={platformLabel(platform)} surface="media" />
            ))}
          </div>
        </div>

        <div className="tf-bottom">
          {/* One island of media glass, like a platform's own tab bar: the
              four actions on one surface instead of four floating squares. */}
          <div
            className="tf-rail"
            role="group"
            aria-label={`Actions for ${trend.title}`}
            data-asleep={asleep ? "" : undefined}
            onFocus={() => {
              railFocused.current = true;
              wake();
            }}
            onBlur={() => {
              railFocused.current = false;
              wake();
            }}
          >
            <RailButton
              name="Sound"
              on={soundOn}
              disabled={!canHear}
              onClick={() => onToggleSound(currentVideo.current)}
            >
              {soundOn ? <Volume2 className="size-5" aria-hidden="true" /> : <VolumeX className="size-5" aria-hidden="true" />}
            </RailButton>
            {canSave ? (
              <RailButton
                name={saved ? `${trend.title} is saved to references` : `Save ${trend.title} to references`}
                done={saved}
                onClick={() => onSave(trend)}
              >
                {saved ? (
                  <BookmarkCheck className="size-5" aria-hidden="true" />
                ) : (
                  <BookmarkPlus className="size-5" aria-hidden="true" />
                )}
              </RailButton>
            ) : null}
            {onRecreate ? (
              <RailButton name={`Recreate ${trend.title} in Workshop`} onClick={() => onRecreate(trend)}>
                <RemixMark className="size-5" aria-hidden="true" />
              </RailButton>
            ) : null}
            <RailButton name={`Open ${trend.title}`} onClick={() => onDetail(trend.id)}>
              <Maximize2 className="size-5" aria-hidden="true" />
            </RailButton>
          </div>

          <div className="tf-words">
            <h2 className="tf-title">{trend.title}</h2>
            {trend.body?.trim() ? <p className="tf-reason">{trend.body}</p> : null}
            {clips.length > 1 ? (
              <p className="tf-examples">
                <span className="tf-dots" aria-hidden="true">
                  {clips.map((clip, i) => (
                    <span
                      key={`${clip.videoUrl ?? clip.thumbnailUrl ?? "dot"}-${i}`}
                      className={cn("tf-dot", i === slide && "tf-dot--on")}
                    />
                  ))}
                </span>
                <span className="tt-label uppercase">
                  Example {slide + 1} of {clips.length}
                </span>
              </p>
            ) : null}
          </div>
        </div>
      </div>

      <div className="tf-strip">
        <div className="tf-figures" aria-hidden="true">
          <div className="tf-fig">
            <span className={cn("tf-fig-value", views === null && "tf-fig-value--none")}>
              {views === null ? "Not counted" : `${compactTrendNum(views)}+`}
            </span>
            <span className="tf-fig-label">Views</span>
          </div>
          <div className="tf-fig">
            <span className={cn("tf-fig-value", likes === null && "tf-fig-value--none")}>
              {likes === null ? "Not counted" : `${compactTrendNum(likes)}+`}
            </span>
            <span className="tf-fig-label">Likes</span>
          </div>
          <div className="tf-fig">
            <span className="tf-fig-value">{compactAge(trend.createdAt)}</span>
            <span className="tf-fig-label">Found</span>
          </div>
          <div className="tf-fig tf-fig--end">
            <span className="tf-fig-value">
              {index + 1} / {total}
            </span>
            <span className="tf-fig-label">{index === 0 ? "Swipe up" : ""}</span>
          </div>
        </div>
        <p className="sr-only">
          {figuresSentence(views, likes, trend.createdAt)} Trend {index + 1} of {total} loaded.
        </p>
      </div>
    </article>
  );
}

export default memo(PhoneFeedItem);
